import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type {
	AiFeatureRequestEnvelopeConfluencePageEmojiSuggestionRequest,
	ConfluencePageEmojiSuggestionError,
	ConfluencePageEmojiSuggestionErrorReason,
	ConfluencePageEmojiSuggestionResponse,
} from '@atlassian/convo-ai-api/convo-api';

import type {
	AIPageEmojiSuggestionStatus,
	SuggestPageEmojiBaseParams,
	SuggestPageEmojiProps,
	UseAIPageEmojiSuggestionProps,
	UseAIPageEmojiSuggestionReturn,
} from '../types';

const CONVO_AI_PAGE_EMOJI_URL =
	'/gateway/api/assist/api/ai/v2/ai-feature/confluence-page-emoji-suggestion';

// ConfluencePageEmojiSuggestionError (from generated @atlassian/convo-ai-api) requires
// both error_reason and error_message. FE never surfaces this string to users.
const GENERIC_API_ERROR_MESSAGE = 'Failed to suggest page emoji';

class SuggestEmojiError extends Error {
	constructor(public readonly apiError: ConfluencePageEmojiSuggestionError) {
		super(apiError.error_message);
		this.name = 'SuggestEmojiError';
	}
}

export const useAIPageEmojiSuggestion = ({
	contentId,
	eventCallbacks,
}: UseAIPageEmojiSuggestionProps): UseAIPageEmojiSuggestionReturn => {
	const [loading, setLoading] = useState(false);
	const [data, setData] = useState<ConfluencePageEmojiSuggestionResponse | null>(null);
	const [error, setError] = useState<ConfluencePageEmojiSuggestionError | null>(null);
	const [aiEmojiStatus, setAiEmojiStatus] = useState<AIPageEmojiSuggestionStatus>('NOT_LOADING');

	const abortControllerRef = useRef<AbortController | null>(null);

	// Held in a ref so suggestEmoji's identity stays stable across renders.
	const callbacksRef = useRef(eventCallbacks);
	useEffect(() => {
		callbacksRef.current = eventCallbacks;
	}, [eventCallbacks]);

	const reset = useCallback(() => {
		setLoading(false);
		setData(null);
		setError(null);
		setAiEmojiStatus('NOT_LOADING');
	}, []);

	const suggestEmoji = useCallback(
		async ({ title, locale }: SuggestPageEmojiProps): Promise<string | undefined> => {
			const trimmedTitle = (title ?? '').trim();
			const baseParams: SuggestPageEmojiBaseParams = {
				contentId,
				titleLength: trimmedTitle.length,
				...(locale !== undefined && { locale }),
			};

			if (!trimmedTitle) {
				const invalidError: ConfluencePageEmojiSuggestionError = {
					error_reason: 'invalid_input',
					error_message: GENERIC_API_ERROR_MESSAGE,
				};
				setError(invalidError);
				setData(null);
				setAiEmojiStatus('INVALID_INPUT');
				callbacksRef.current?.onSuggestError?.({
					...baseParams,
					error: new Error(invalidError.error_message),
					errorReason: 'invalid_input',
				});
				return undefined;
			}

			callbacksRef.current?.onSuggestStart?.(baseParams);

			abortControllerRef.current?.abort();
			abortControllerRef.current = new AbortController();

			setError(null);
			setData(null);
			setLoading(true);
			setAiEmojiStatus('LOADING');

			try {
				const request: AiFeatureRequestEnvelopeConfluencePageEmojiSuggestionRequest = {
					ai_feature_input: {
						title: trimmedTitle,
						...(locale !== undefined && { locale }),
					},
				};

				const response = await fetch(CONVO_AI_PAGE_EMOJI_URL, {
					method: 'POST',
					headers: {
						'content-type': 'application/json;charset=UTF-8',
						'X-Experience-Id': 'confluence-ai-first-creation',
						'X-Product': 'confluence',
					},
					body: JSON.stringify(request),
					signal: abortControllerRef.current.signal,
				});

				if (!response.ok) {
					throw new SuggestEmojiError({
						error_reason: 'api_error',
						error_message: GENERIC_API_ERROR_MESSAGE,
					});
				}

				const parsed = (await response.json()) as {
					ai_feature_output?: ConfluencePageEmojiSuggestionResponse;
				};
				const output = parsed?.ai_feature_output;

				if (!output || typeof output.status !== 'string') {
					throw new SuggestEmojiError({
						error_reason: 'api_error',
						error_message: GENERIC_API_ERROR_MESSAGE,
					});
				}

				setData(output);

				switch (output.status) {
					case 'suggested':
					case 'default': {
						if (!output.emojiId) {
							throw new SuggestEmojiError({
								error_reason: 'api_error',
								error_message: GENERIC_API_ERROR_MESSAGE,
							});
						}
						setError(null);
						setAiEmojiStatus('SUCCESSFULLY_SET');
						callbacksRef.current?.onSuggestSuccess?.({
							...baseParams,
							status: output.status,
							emojiId: output.emojiId,
						});
						return output.emojiId;
					}

					case 'no_suggestion': {
						setError(null);
						setAiEmojiStatus('NO_SUGGESTION');
						callbacksRef.current?.onSuggestNoSuggestion?.({
							...baseParams,
							status: 'no_suggestion',
						});
						return undefined;
					}

					case 'error': {
						// BE always returns 200 for AIFeature envelopes; throw so the single catch handles it.
						throw new SuggestEmojiError(
							output.error ?? {
								error_reason: 'api_error',
								error_message: GENERIC_API_ERROR_MESSAGE,
							},
						);
					}

					case 'loading':
					default: {
						setError(null);
						setAiEmojiStatus('NOT_LOADING');
						return undefined;
					}
				}
			} catch (err) {
				const isErrorType = err instanceof Error;

				if (isErrorType && err.name === 'AbortError') {
					callbacksRef.current?.onSuggestAbort?.(baseParams);
					return undefined;
				}

				const apiError: ConfluencePageEmojiSuggestionError =
					err instanceof SuggestEmojiError
						? err.apiError
						: {
								error_reason: 'api_error',
								error_message: GENERIC_API_ERROR_MESSAGE,
							};
				const errorReason: ConfluencePageEmojiSuggestionErrorReason = apiError.error_reason;

				setError(apiError);
				setData({ status: 'error', error: apiError });
				setAiEmojiStatus('ERROR');
				callbacksRef.current?.onSuggestError?.({
					...baseParams,
					error: isErrorType ? err : new Error(apiError.error_message),
					errorReason,
				});
				return undefined;
			} finally {
				setLoading(false);
			}
		},
		// eventCallbacks is intentionally excluded — read through callbacksRef for stable identity.
		[contentId],
	);

	const isRetryable = useMemo(
		() => aiEmojiStatus === 'ERROR' && error?.error_reason === 'api_error',
		[aiEmojiStatus, error],
	);

	const aiEmojiId = useMemo(
		() =>
			data && (data.status === 'suggested' || data.status === 'default') ? data.emojiId : undefined,
		[data],
	);

	return {
		suggestEmoji,
		aiEmojiId,
		aiEmojiReasoning: data?.reasoning,
		aiEmojiStatus,
		data,
		error,
		isRetryable,
		loading,
		reset,
	};
};
